import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Theme } from "../data/style/Theme";
import * as routes from "../data/links/routes";
import { getWorkoutTypes } from "../api/helpers";
import WodDisplay from "../components/WodDisplay";
import Loader from "../components/Display/Loader";
import { errorTexts } from "../data/assets/texts";
import { Link, useNavigate } from "react-router-dom";
import useScreenSize from "../context/ScreenContext";
import { getExerciseCategories } from "../api/helpers";
import NoDataDiv from "../components/Display/NoDataDiv";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNotifications } from "../context/NotificationContext";
import WorkoutLeaderboard from "../components/WorkoutLeaderboard";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { getMostPerformedWorkout, getPublicWorkouts } from "../api/workouts";
import {
  AllCenteredColumn,
  SecondaryTitle,
  CenteredRow,
  scrollBar,
  MainTitle,
  SpacedRow,
  MainText,
} from "../data/style/Style";
import {
  EXERCISE_CATEGORIES_DATA,
  WORKOUT_TYPES_DATA,
} from "../data/assets/arrays";

const PublicWorkouts = () => {
  const navigate = useNavigate();
  const [limit, setLimit] = useState(0);
  const [typeId, setTypeId] = useState(-1);
  const [hasMore, setHasMore] = useState(true);
  const [workouts, setWorkouts] = useState([]);
  const [categoryId, setCategoryId] = useState(-1);
  const { enableNotification } = useNotifications();
  const { mobileDevice, size } = useScreenSize();
  var [width] = size;

  const { data: categories, isPending: categoriesPending } = useQuery({
    queryKey: ["exercise", "categories"],
    queryFn: ({ signal }) => getExerciseCategories({ signal, wodable: 1 }),
    staleTime: 12 * 60 * 60 * 1000,
  });

  const { data, isPending } = useQuery({
    queryKey: ["workout", "trending", "level"],
    queryFn: ({ signal }) => getMostPerformedWorkout({ signal }),
    staleTime: 12 * 60 * 60 * 1000,
  });

  const { data: types, isPending: typesPending } = useQuery({
    queryKey: ["workout", "types"],
    queryFn: ({ signal }) => getWorkoutTypes({ signal }),
  });

  const { mutate: fetchPublicWorkouts } = useMutation({
    mutationFn: getPublicWorkouts,
    onSuccess: (response) => {
      if (response.data.workouts.length < 10) {
        setHasMore(false);
      }
      setWorkouts((prev) => [...prev, ...response.data.workouts]);
      loadingRef.current = false;
    },
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }
      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }
      if (error.response.status >= 500) {
        enableNotification(errorTexts.system_error, "error");
      }
    },
  });

  const loadingRef = useRef(false);

  useEffect(() => {
    if (hasMore) fetchPublicWorkouts({ typeId, categoryId, limit });
  }, [typeId, categoryId, limit, hasMore]);

  const handleScroll = useCallback(
    (e) => {
      if (loadingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 300;

      if (nearBottom && hasMore) {
        loadingRef.current = true;
        setLimit((prev) => prev + 10);
      }
    },
    [hasMore],
  );

  useEffect(() => {
    setHasMore(true);
    setLimit(0);
    setWorkouts([]);
  }, [typeId, categoryId]);

  let workoutContent = <Loader height={"90%"} />;

  if (workouts.length > 0) {
    workoutContent = (
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: mobileDevice
            ? "repeat(auto-fit, minmax(275px, 1fr))"
            : "repeat(auto-fit, minmax(370px, 1fr))",
          pb: 2,
        }}
      >
        {workouts.map((workoutId) => (
          <Box
            key={workoutId}
            maxWidth={"465px"}
            minHeight={!mobileDevice && "250px"}
          >
            <WodDisplay wodId={workoutId} setWorkouts={setWorkouts} />
          </Box>
        ))}
      </Box>
    );
  } else {
    workoutContent = (
      <NoDataDiv
        text={"No public workouts for this level."}
        minHeight={"300px"}
        height={"85%"}
      />
    );
  }

  return (
    <Stack
      gap={2}
      pr={1}
      height={"100%"}
      overflow={"auto"}
      onScroll={handleScroll}
      sx={scrollBar}
    >
      <Stack
        direction={"row"}
        justifyContent={"center"}
        flexWrap={"wrap"}
        gap={3}
      >
        <Stack gap={1} width={width < 900 ? "92%" : "32%"}>
          <MainTitle>Most Performed workout</MainTitle>
          <Box
            width={"100%"}
            maxWidth={"465px"}
            minHeight={!mobileDevice && "250px"}
          >
            {!isPending && (
              <>
                {data !== 0 ? (
                  <WodDisplay wodId={data} />
                ) : (
                  <NoDataDiv
                    text={"No workout found for this level."}
                    height={"100%"}
                    icon={
                      <FitnessCenterIcon
                        sx={{
                          fontSize: width < 750 ? "3rem" : "4rem",
                          color: Theme.palette.orange,
                        }}
                      />
                    }
                  />
                )}
              </>
            )}
          </Box>
        </Stack>
        <Stack width={width < 900 ? "92%" : "32%"}>
          {!isPending && data !== 0 && (
            <WorkoutLeaderboard gd={0} workoutId={data} isTrending={true} />
          )}
        </Stack>
        <Stack width={width < 900 ? "92%" : "32%"}>
          {!isPending && data !== 0 && (
            <WorkoutLeaderboard gd={1} workoutId={data} isTrending={true} />
          )}
        </Stack>
      </Stack>
      <Stack gap={1} pl={1} mt={2}>
        <SpacedRow alignItems={"center"} gap={2} flexWrap={"wrap"}>
          <MainTitle>Public Workouts</MainTitle>
          {!typesPending && (
            <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
              <AllCenteredColumn
                onClick={() => setTypeId(-1)}
                sx={{
                  cursor: "pointer",
                  background:
                    typeId === -1
                      ? `${Theme.palette.blue}22`
                      : Theme.palette.card,
                  border: 1,
                  borderRadius: 3,
                  borderColor:
                    typeId === -1
                      ? `${Theme.palette.blue}55`
                      : Theme.palette.border,
                  p: 1,
                  transition: "300ms",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <SecondaryTitle
                  sx={{
                    color: -1 === typeId && Theme.palette.blue + " !important",
                  }}
                >
                  All
                </SecondaryTitle>
              </AllCenteredColumn>
              {types.map((item) => {
                const icon = WORKOUT_TYPES_DATA.find(
                  (type) => type.id === item.id,
                ).icon;
                return (
                  <AllCenteredColumn
                    onClick={() => setTypeId(item.id)}
                    sx={{
                      cursor: "pointer",
                      background:
                        item.id === typeId
                          ? `${item.color}22`
                          : Theme.palette.card,
                      border: 1,
                      borderRadius: 3,
                      borderColor:
                        item.id === typeId
                          ? `${item.color}55`
                          : Theme.palette.border,
                      p: 1,
                      transition: "300ms",
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <span
                      style={{ fontSize: "0.9rem", color: Theme.palette.text }}
                    >
                      {icon}
                    </span>
                    <SecondaryTitle
                      sx={{
                        color: item.id === typeId && item.color + " !important",
                        fontSize: "0.9rem",
                      }}
                    >
                      {item.name}
                    </SecondaryTitle>
                  </AllCenteredColumn>
                );
              })}
            </Stack>
          )}
          {!categoriesPending && (
            <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
              {categories.map((item) => {
                const icon = EXERCISE_CATEGORIES_DATA.find(
                  (type) => type.id === item.id,
                ).icon;
                return (
                  <CenteredRow
                    gap={1}
                    onClick={() => setCategoryId(item.id)}
                    sx={{
                      cursor: "pointer",
                      background:
                        item.id === categoryId
                          ? `${Theme.palette.orange}22`
                          : Theme.palette.card,
                      border: 1,
                      borderRadius: 3,
                      borderColor:
                        item.id === categoryId
                          ? Theme.palette.orange
                          : Theme.palette.border,
                      p: 1,
                      transition: "300ms",

                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <span
                      style={{ fontSize: "0.9rem", color: Theme.palette.text }}
                    >
                      {icon}
                    </span>
                    <MainText
                      sx={{
                        color:
                          item.id === categoryId &&
                          Theme.palette.orange + " !important",
                        fontSize: "0.9rem",
                      }}
                    >
                      {item.name}
                    </MainText>
                  </CenteredRow>
                );
              })}
            </Stack>
          )}
        </SpacedRow>
        <Stack alignItems={"end"}>
          <Link to={routes.USER_WORKOUTS} style={{ textDecoration: "none" }}>
            <span
              style={{
                fontFamily: "DM Sans",

                color: "#f97316",
              }}
            >
              Saved Workouts →
            </span>
          </Link>
        </Stack>
      </Stack>
      {workoutContent}
    </Stack>
  );
};
export default PublicWorkouts;
